import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { LayoutGrid, Loader2, Info } from "lucide-react";

// Occupazione 0% = verde, 100% = rosso (interpolazione HSL 140→0).
const occStyle = (v, sold) => {
  if (sold) return { bg: "#b91c1c", fg: "#fff" };
  if (v == null) return { bg: "rgba(148,163,184,0.10)", fg: "#94a3b8" };
  const hue = Math.round(140 - (140 * Math.max(0, Math.min(100, v))) / 100);
  return { bg: `hsl(${hue}, 68%, 42%)`, fg: "#fff" };
};

const MONTHS_IT = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const MONTHS_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function CompetitorOccupancyHeatmap({ propId, lang = "it", refreshKey = 0 }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!propId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propId}/competitor-occupancy-grid?lang=${lang}`);
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [propId, lang]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const dates = data?.dates || [];
  const comps = data?.competitors || [];

  const fmtDay = (iso) => { try { const [, m, d] = iso.split("-"); return `${parseInt(d, 10)}`; } catch { return iso; } };
  const monthOf = (iso) => { try { return parseInt(iso.split("-")[1], 10) - 1; } catch { return 0; } };
  const isWeekend = (iso) => { try { const wd = new Date(iso + "T00:00:00").getDay(); return wd === 0 || wd === 5 || wd === 6; } catch { return false; } };

  // Raggruppa le colonne per mese per l'header superiore.
  const monthSpans = [];
  dates.forEach((iso) => {
    const m = monthOf(iso);
    const last = monthSpans[monthSpans.length - 1];
    if (last && last.m === m) last.span += 1;
    else monthSpans.push({ m, span: 1 });
  });
  const MON = lang === "en" ? MONTHS_EN : MONTHS_IT;

  return (
    <motion.div
      data-testid="competitor-occupancy-heatmap"
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="mb-6 rounded-lg border border-border bg-card p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <LayoutGrid className="w-4 h-4 text-primary" />
          <h4 className="text-sm font-semibold">{L("Occupazione competitor — 6 mesi", "Competitor occupancy — 6 months")}</h4>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span>{L("Bassa", "Low")}</span>
          <span className="inline-block h-3 w-24 rounded" style={{ background: "linear-gradient(90deg, hsl(140,68%,42%), hsl(70,68%,42%), hsl(0,68%,42%))" }} />
          <span>{L("Alta", "High")}</span>
        </div>
      </div>

      {loading && !data ? (
        <div className="py-8 text-center text-muted-foreground text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{L("Carico l'occupazione…", "Loading occupancy…")}</div>
      ) : !dates.length || !comps.length ? (
        <div className="py-6 text-center text-sm text-muted-foreground" data-testid="occ-heatmap-empty">
          <Info className="w-4 h-4 inline mr-1.5" />
          {L("Nessun dato di occupazione ancora. Seleziona i competitor e avvia una scansione.", "No occupancy data yet. Select competitors and run a scan.")}
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid="occ-heatmap-scroll">
          <table className="border-separate" style={{ borderSpacing: 0 }}>
            <thead>
              <tr>
                <th className="sticky left-0 z-20 bg-card text-left text-[11px] font-medium text-muted-foreground px-2 py-1" style={{ minWidth: 160 }}>
                  {L("Competitor", "Competitor")}
                </th>
                {monthSpans.map((ms, i) => (
                  <th key={i} colSpan={ms.span} className="text-[11px] font-semibold text-muted-foreground text-left px-1 py-1 border-l border-border">
                    {MON[ms.m]}
                  </th>
                ))}
              </tr>
              <tr>
                <th className="sticky left-0 z-20 bg-card px-2 py-1" style={{ minWidth: 160 }} />
                {dates.map((iso) => (
                  <th key={iso} className={`text-[10px] font-normal px-0.5 py-1 text-center ${isWeekend(iso) ? "text-primary font-semibold" : "text-muted-foreground"}`} style={{ minWidth: 26 }}>
                    {fmtDay(iso)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comps.map((c) => (
                <tr key={c.competitor_id} data-testid={`occ-row-${c.competitor_id}`}>
                  <td className="sticky left-0 z-10 bg-card text-xs font-medium px-2 py-1 truncate border-t border-border" style={{ minWidth: 160, maxWidth: 200 }} title={c.name}>
                    {c.name}
                  </td>
                  {dates.map((iso) => {
                    const cell = c.cells?.[iso];
                    const v = cell?.occupancy_est;
                    const st = occStyle(v, cell?.sold_out);
                    const title = cell
                      ? `${c.name}\n${iso}\n${L("Occupazione", "Occupancy")}: ${v != null ? v + "%" : "n.d."}` +
                        `${cell.rooms_available != null ? `\n${L("Camere libere", "Rooms free")}: ${cell.rooms_available}/${cell.total_rooms || "?"}` : ""}` +
                        `${cell.price_net != null ? `\n${L("Prezzo netto", "Net price")}: €${cell.price_net}${c.calibration_pct == null ? L(" (nessuno sconto impostato)", " (no discount set)") : ""}` : ""}` +
                        `${cell.price != null ? `\n${L("Lordo Booking", "Booking gross")}: €${Math.round(cell.price)}` : ""}` +
                        `${cell.breakfast ? `\n${L("Colazione inclusa", "Breakfast included")}` : ""}` +
                        `${cell.detected_at ? `\n${L("Rilevato", "Detected")}: ${new Date(cell.detected_at).toLocaleDateString()}` : ""}`
                      : `${iso}\n${L("Nessun dato", "No data")}`;
                    return (
                      <td key={iso} className="p-0.5 border-t border-border" style={{ minWidth: 26 }}>
                        <div
                          className="h-6 rounded flex items-center justify-center text-[9px] font-semibold cursor-default"
                          style={{ backgroundColor: st.bg, color: st.fg }}
                          title={title}
                          data-testid={`occ-cell-${c.competitor_id}-${iso}`}
                        >
                          {cell?.sold_out ? "SO" : v != null ? v : ""}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-muted-foreground mt-2">
            {L("Numero = % occupazione stimata · SO = sold out · celle vuote = data non scansionata. Passa il mouse per camere e prezzo. Dati dal database (nessuna chiamata Apify).",
               "Number = estimated occupancy % · SO = sold out · empty cells = date not scanned. Hover for rooms and price. Data from the database (no Apify calls).")}
          </p>
        </div>
      )}
    </motion.div>
  );
}
